import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  Checkbox,
  Dialog,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useCallback, useEffect, useState } from "react";
import SplitEmployeeDocumentFileRow from "./SplitEmployeeDocumentFileRow/SplitEmployeeDocumentFileRow";
import { StyledTableHead } from "./styles";
import { FilesIcon } from "../../../assets/icons";
import { Page } from "../../../models/api/page";
import { SplitEmployeeDocumentDTO } from "../../../models/split-document/split-employee-document/split-employee-document-dto";
import { SplitEmployeeDocumentFileDTO } from "../../../models/split-document/split-employee-document/split-employee-document-file-dto";
import { SplitEmployeeDocumentFileFilter } from "../../../models/split-document/split-employee-document/split-employee-document-file-filter";
import { ProcessSplitEmployeeDocumentApiService } from "../../../services/split-document/split-employee-document/process-split-employee-document-api.service";
import { SplitEmployeeDocumentFilesApiService } from "../../../services/split-document/split-employee-document/split-employee-document-files-api.service";
import ConfirmationChangesDialog from "../../ConfirmationChangesDialog/ConfirmationChangesDialog";
import CustomConfirmationChangesDialog from "../../CustomConfirmationChangesDialog/CustomConfirmationChangesDialog";
import DialogActions from "../../DialogActions/DialogActions";
import DialogTitle from "../../DialogTitle/DialogTitle";
import LoadingOverlay from "../../LoadingOverlay/LoadingOverlay";
interface Props {
  item: SplitEmployeeDocumentDTO;
  open?: boolean;
  triggeredByParent?: boolean;
  buttonType?: "icon" | "text";
  resplit?: boolean;
}

interface State {
  open: boolean;
  rawEmployeeFiles: SplitEmployeeDocumentFileDTO[];
  employeeFiles: SplitEmployeeDocumentFileDTO[];
  loading: boolean;
  error: string;
  isCCDOpen: boolean;
  isCCCDOpen: boolean;
  selectedEmployeeFiles: string[];
}

const SplitEmployeeDocumentFiles = ({
  item,
  open,
  triggeredByParent = false,
  buttonType = "icon",
  resplit = false,
}: Props) => {
  const theme = useTheme();

  const [state, setState] = useState<State>({
    open: open ?? false,
    rawEmployeeFiles: [],
    employeeFiles: [],
    loading: false,
    error: "",
    isCCDOpen: false,
    isCCCDOpen: false,
    selectedEmployeeFiles: [],
  });

  const loadData = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));
    try {
      const response = await new SplitEmployeeDocumentFilesApiService().loadItems(
        {
          splitEmployeeDocumentId: item.id,
        } as SplitEmployeeDocumentFileFilter,
        undefined,
        undefined,
        {
          pageSize: 10000,
          pageNumber: 0,
        } as Page,
        undefined,
        60,
        undefined
      );
      if (response.error) {
        setState((state) => ({
          ...state,
          error: response.error,
        }));
      } else {
        setState((state) => ({
          ...state,
          employeeFiles: response.items,
          rawEmployeeFiles: response.items,
        }));
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error,
      }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [item.id]);

  const handleSaveUpdatedFile = useCallback(
    (mutatedItem: SplitEmployeeDocumentFileDTO) => {
      const updatedItems = [...state.employeeFiles];
      const index = updatedItems.findIndex((item) => item.id === mutatedItem.id);
      if (index !== -1) {
        updatedItems[index] = { ...updatedItems[index], ...mutatedItem };
        setState((state) => ({
          ...state,
          employeeFiles: [...updatedItems],
        }));
      }
    },
    [state.employeeFiles]
  );

  const handleSave = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));
    try {
      const response = await new SplitEmployeeDocumentFilesApiService().saveItems([...state.employeeFiles]);
      if (response.error) {
        setState((state) => ({
          ...state,
          error: response.error,
        }));
      } else {
        setState((state) => ({ ...state, open: false }));
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error,
      }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [state.employeeFiles]);

  const splitDocument = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));
    try {
      const response = await new ProcessSplitEmployeeDocumentApiService().loadItems({
        splitEmployeeDocumentId: item.id,
      } as SplitEmployeeDocumentFileFilter);
      if (response.error) {
        setState((state) => ({
          ...state,
          error: response.error,
        }));
      } else {
        setState((state) => ({
          ...state,
          employeeFiles: response.items[0].employeeDocuments,
          rawEmployeeFiles: response.items[0].employeeDocuments,
        }));
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error,
      }));
    } finally {
      setState((state) => ({
        ...state,
        loading: false,
      }));
    }
  }, [item.id]);

  useEffect(() => {
    if (state.open && !resplit && !triggeredByParent) loadData();
    if (state.open && triggeredByParent) splitDocument();
  }, [resplit, state.open, triggeredByParent]);

  const handleSelectAllEmployeeFiles = useCallback(() => {
    //unselect
    state.selectedEmployeeFiles.length > 0
      ? setState((state) => ({
          ...state,
          selectedEmployeeFiles: [],
          employeeFiles: [
            ...state.employeeFiles.map((eeFile) => ({
              ...eeFile,
              isApproved: false,
              isRejected: true,
            })),
          ],
        }))
      : //select all
        setState((state) => ({
          ...state,
          selectedEmployeeFiles: state.employeeFiles
            .filter((eeFile) => eeFile.employee)
            .map((employeeFile) => employeeFile.id),
          employeeFiles: [
            ...state.employeeFiles
              .filter((eeFile) => eeFile.employee)
              .map((eeFile) => ({
                ...eeFile,
                isApproved: true,
                isRejected: false,
              })),
            ...state.employeeFiles.filter((eeFile) => !eeFile.employee),
          ],
        }));
  }, [state.selectedEmployeeFiles.length]);

  const handleSelectemployeeFile = useCallback(
    (updatedEeFile: SplitEmployeeDocumentFileDTO) => {
      const updatedItems = [...state.employeeFiles];
      const index = updatedItems.findIndex((item) => item.id === updatedEeFile.id);

      if (index !== -1) {
        updatedItems[index] = {
          ...updatedItems[index],
          ...{
            ...updatedEeFile,
            isApproved: !updatedEeFile.isApproved,
            isRejected: !updatedEeFile.isRejected,
          },
        };
        setState((state) => ({
          ...state,
          selectedEmployeeFiles: state.selectedEmployeeFiles.includes(updatedEeFile.id)
            ? [...state.selectedEmployeeFiles.filter((id) => updatedEeFile.id !== id)]
            : [...state.selectedEmployeeFiles, updatedEeFile.id],
          employeeFiles: [...updatedItems],
        }));
      }
    },
    [state.employeeFiles]
  );

  return (
    <>
      {triggeredByParent ? (
        <></>
      ) : buttonType === "icon" ? (
        <IconButton
          onClick={() => setState((state) => ({ ...state, open: true }))}
          style={{
            color: theme.palette.primary.main,
          }}
        >
          <FilesIcon />
        </IconButton>
      ) : (
        <Button
          onClick={() =>
            setState((state) => ({
              ...state,
              ...(!resplit ? { open: true } : { isCCCDOpen: true }),
            }))
          }
          style={{
            textTransform: "none",
            color: theme.palette.text.primary,
          }}
        >
          <Grid style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <Typography>Split employee document</Typography>
            {item.splitOn ? (
              <FontAwesomeIcon
                icon={faCircleCheck}
                color={theme.palette.success.main}
              />
            ) : (
              <></>
            )}
          </Grid>
        </Button>
      )}

      <Dialog
        open={state.open}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          title="Split employee document files"
          handleClose={() => setState((state) => ({ ...state, isCCDOpen: true }))}
        />
        <LoadingOverlay
          error={state.error}
          loading={state.loading}
        >
          <Table>
            <StyledTableHead>
              <TableRow>
                <TableCell align="center">
                  <Checkbox
                    checked={state.selectedEmployeeFiles.length === state.employeeFiles.length}
                    indeterminate={state.employeeFiles.length > 0}
                    onChange={handleSelectAllEmployeeFiles}
                  />
                </TableCell>
                {["File", "Employee", "Name matches", "Birthdate matches", "Address matches", "Tax ID matches"].map(
                  (field) => (
                    <TableCell
                      align="center"
                      key={field}
                    >
                      {field}
                    </TableCell>
                  )
                )}
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {state.employeeFiles.map((employeefile, idx) => (
                <React.Fragment key={idx}>
                  <SplitEmployeeDocumentFileRow
                    splitDocumentFile={employeefile}
                    handleSaveUpdate={handleSaveUpdatedFile}
                    legalEntity={item.legalEntity}
                    handleSelectionChange={handleSelectemployeeFile}
                  />
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
          <DialogActions
            handleSave={handleSave}
            handleCancel={() => setState((state) => ({ ...state, isCCDOpen: true }))}
          />
        </LoadingOverlay>
      </Dialog>
      <ConfirmationChangesDialog
        open={state.isCCDOpen}
        handleCancel={() => setState((state) => ({ ...state, isCCDOpen: false }))}
        handleDiscard={() => setState((state) => ({ ...state, open: false, isCCDOpen: false }))}
      />
      <CustomConfirmationChangesDialog
        title="Split employee document"
        loading={state.loading}
        handleCancel={() => setState((state) => ({ ...state, isCCCDOpen: false }))}
        open={state.isCCCDOpen}
        actionsAndHandlers={[
          {
            action: "split",
            handler: () => {
              setState((state) => ({ ...state, open: true, isCCCDOpen: false }));
              splitDocument();
            },
            customButtonStyle: {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              textTransform: "none",
            },
          },
        ]}
      >
        <Typography>
          Are you sure you want to split the document again ? This will result in the loss of previous split data
        </Typography>
      </CustomConfirmationChangesDialog>
    </>
  );
};

export default SplitEmployeeDocumentFiles;
